import { z } from "zod/v4";

import { DeviceTypeList } from "../schemas/analytics/base";

import { AnalyticsProductDtoSchema } from "./analytics-products";
import { AudienceDtoSchema } from "./audiences";
import { ExperienceActionDtoSchema } from "./experience-actions";
import {
  CreateExperienceAnalysisSchema,
  ExperienceAnalysisDtoSchema,
} from "./experience-analysis";
import { ExperienceCategoryList } from "./experience-categories";
import { ExperienceCustomMetricDtoSchema } from "./experience-custom-metrics";
import {
  CreateExperienceIntegrationDtoSchema,
  ExperienceIntegrationDtoSchema,
} from "./experience-integrations";
import { ExperienceKeyMetricDtoSchema } from "./experience-key-metrics";
import { ExperiencePageTargetingDtoSchema } from "./experience-page-targeting";
import { ExperienceProductDtoSchema } from "./experience-products";
import { OfferProductDtoSchema } from "./offer-products";
import { OfferDtoSchema } from "./offers";
import { ProductVariantProductTypeList } from "./product-variants";
import { S3FileLocationDtoSchema } from "./s3-file-location";
import { ShippingTestMethodDefinitionDtoSchema } from "./shipping-test-method-definitions";
import { UserInterfaceDtoSchema } from "./user-interfaces";
import { VariationDtoSchema } from "./variations";

export const ExperienceDevicePreviewDtoSchema = z
  .object({
    device: z
      .enum(DeviceTypeList)
      .default("desktop")
      .describe("Device type for preview: 'desktop', 'mobile', or 'tablet'"),
    path: z
      .string()
      .default("/")
      .describe("URL path to use for preview (e.g., '/products/example')"),
  })
  .describe("Device and path configuration for previewing the experience");
export type ExperienceDevicePreviewDto = z.infer<
  typeof ExperienceDevicePreviewDtoSchema
>;

export type HasExperienceTypeProperty =
  | "hasTestPricing"
  | "hasTestShipping"
  | "hasTestDiscount";

export const ExperienceStatusList = [
  "pending",
  "started",
  "ended",
  "paused",
] as const;
export type ExperienceStatus = (typeof ExperienceStatusList)[number];

export const PersonalizationStatusList = [
  "pending",
  "started",
  "ended",
  "paused",
] as const;
export type PersonalizationStatus = (typeof PersonalizationStatusList)[number];

export const ExperienceTypeList = [
  "content/advanced",
  "content/onsiteEdits",
  "content/url",
  "content/theme",
  "content/template",
  "pricing",
  "shipping",
  "personalization",
  "personalization/offer",
  "sitewide",
] as const;
export type ExperienceType = (typeof ExperienceTypeList)[number];

export const SourceActionTypeList = ["duplicate", "rollout"] as const;
export type SourceActionType = (typeof SourceActionTypeList)[number];

export const ExperienceCurrencyDtoSchema = z.object({
  code: z
    .string()
    .default("USD")
    .describe("ISO 4217 currency code (e.g., 'USD', 'EUR', 'GBP')"),
  country: z
    .string()
    .optional()
    .describe("Country code for currency formatting"),
  options: z
    .any()
    .optional()
    .describe("Additional currency formatting options"),
  symbol: z
    .string()
    .default("$")
    .describe("Currency symbol to display (e.g., '$', '€', '£')"),
  suffix: z
    .string()
    .optional()
    .describe("Currency suffix if needed (e.g., for display after amount)"),
});

export type ExperienceCurrencyDto = z.infer<typeof ExperienceCurrencyDtoSchema>;

export const ExperienceTestTypeDtoSchema = z.object({
  hasTestPricing: z
    .boolean()
    .describe("Whether pricing variations are enabled"),
  hasTestShipping: z
    .boolean()
    .describe("Whether shipping variations are enabled"),
  hasTestCampaign: z
    .boolean()
    .describe("Whether campaign/offer variations are enabled"),
  hasTestContent: z
    .boolean()
    .describe("Whether any content variations are enabled"),
  hasTestContentUrl: z
    .boolean()
    .describe("Whether URL redirect variations are enabled"),
  hasTestContentAdvanced: z
    .boolean()
    .describe("Whether advanced content variations are enabled"),
  hasTestContentOnsite: z
    .boolean()
    .describe("Whether onsite edit variations are enabled"),
  hasTestContentTemplate: z
    .boolean()
    .describe("Whether template variations are enabled"),
  hasTestContentTheme: z
    .boolean()
    .describe("Whether theme variations are enabled"),
  hasTestOnsiteInjections: z
    .boolean()
    .describe("Whether CSS/JS injection variations are enabled"),
});

export type ExperienceTestTypeDto = z.infer<typeof ExperienceTestTypeDtoSchema>;
export const ExperienceDtoSchema = z.object({
  id: z.uuid().describe("Unique identifier for the experience"),
  name: z.string().describe("Name of the A/B test experience"),
  isPreview: z
    .boolean()
    .optional()
    .default(true)
    .describe("Whether the experience is in preview mode. ALWAYS set to true"),
  description: z
    .string()
    .nullish()
    .describe("Detailed description of what this experience tests"),
  devicePreview: ExperienceDevicePreviewDtoSchema.nullish().describe(
    "Device and path configuration for previewing the experience"
  ),
  organizationId: z.uuid().describe("Organization that owns this experience"),
  type: z
    .enum(ExperienceTypeList)
    .describe(
      "Type of experience: 'content/advanced' for content tests, 'pricing' for price tests, 'shipping' for shipping tests"
    ),
  category: z
    .enum(ExperienceCategoryList)
    .describe(
      "Experience type: 'experiment' for experiments, 'personalization' for personalizations"
    ),
  status: z
    .enum(ExperienceStatusList)
    .describe(
      "Experience status: 'pending' (not started), 'started' (active), 'paused', 'ended'"
    ),
  testTypes: ExperienceTestTypeDtoSchema.describe(
    "Which variation types are enabled: content, pricing, shipping"
  ),
  experiencePageTargeting: ExperiencePageTargetingDtoSchema.array()
    .optional()
    .default([])
    .describe("Page-level targeting rules for where the experience should run"),
  experienceActions: ExperienceActionDtoSchema.array().describe(
    "Actions and events tracked for this experience"
  ),
  experienceAnalysis: ExperienceAnalysisDtoSchema.describe(
    "Analysis results and configuration for the experience"
  ),
  experienceIntegrations: ExperienceIntegrationDtoSchema.array()
    .default([])
    .describe("Third-party integrations connected to this experience"),
  offerProducts: OfferProductDtoSchema.array()
    .optional()
    .default([])
    .describe("Products included/excluded from pricing offers"),
  analyticsProducts: AnalyticsProductDtoSchema.array()
    .optional()
    .default([])
    .describe("Products being tracked for analytics in this experience"),
  experienceProducts: ExperienceProductDtoSchema.array()
    .nullish()
    .default([])
    .describe("Product catalog associated with this experience"),
  experienceCustomMetrics: ExperienceCustomMetricDtoSchema.array()
    .default([])
    .describe("Custom event metrics being tracked for this experience"),
  experienceKeyMetrics: ExperienceKeyMetricDtoSchema.array()
    .default([])
    .describe("Key business metrics being tracked (revenue, conversion, AOV)"),
  currency: ExperienceCurrencyDtoSchema.nullish().describe(
    "Currency code (e.g., 'USD') and display settings"
  ),
  variations: VariationDtoSchema.array()
    .default([])
    .describe("Test variations configured for this experience"),
  createdAtTs: z.number().describe("Timestamp when the experience was created"),
  isDeleted: z
    .boolean()
    .optional()
    .describe("Whether the experience has been soft-deleted"),
  shippingTestMethodDefinitions: ShippingTestMethodDefinitionDtoSchema.array()
    .default([])
    .describe("Shipping rate test configurations"),
  audience: AudienceDtoSchema.nullish().describe(
    "Audience targeting rules for this experience"
  ),
  userInterfaces: UserInterfaceDtoSchema.array()
    .default([])
    .describe("UI widgets and interfaces displayed for this experience"),
  lastUpdateTs: z
    .number()
    .describe("Timestamp of the last update to this experience"),
  previewPath: z
    .string()
    .nullish()
    .describe("URL path to preview the experience (e.g., '/products/example')"),
  isExternal: z
    .boolean()
    .default(false)
    .nullish()
    .describe("Whether the experience is managed externally"),
  startedAtTs: z
    .number()
    .nullable()
    .describe(
      "Timestamp when the experience was started (null if not started)"
    ),
  pausedAtTs: z
    .number()
    .nullable()
    .describe("Timestamp when the experience was paused (null if not paused)"),
  archivedAtTs: z
    .number()
    .optional()
    .nullish()
    .describe(
      "Timestamp when the experience was archived (null if not archived)"
    ),
  requiresLink: z
    .boolean()
    .optional()
    .default(false)
    .describe("Whether the experience requires a special link to access"),
  linkBaseUrl: z
    .string()
    .nullable()
    .describe("Base URL for generating experience links"),
  endedAtTs: z
    .number()
    .nullable()
    .describe("Timestamp when the experience ended (null if still running)"),
  productVariantCount: z
    .number()
    .default(0)
    .describe("Number of product variants included in this experience"),
  sourceExperienceId: z
    .uuid()
    .optional()
    .nullable()
    .describe(
      "ID of the source experience if this was duplicated or rolled out"
    ),
  sourceAction: z
    .enum(SourceActionTypeList)
    .optional()
    .nullable()
    .describe("Action taken from source experience: 'duplicate' or 'rollout'"),
});

export type ExperienceDto = z.infer<typeof ExperienceDtoSchema>;

export const InternalExperienceDtoSchema = ExperienceDtoSchema;

export type InternalExperienceDto = z.infer<typeof InternalExperienceDtoSchema>;

export const CreateExperienceDtoSchema = ExperienceDtoSchema.omit({
  experienceActions: true,
  experiencePageTargeting: true,
  offerProducts: true,
  experienceProducts: true,
  variations: true,
  audience: true,
  userInterfaces: true,
  createdAtTs: true,
  organizationId: true,
  lastUpdateTs: true,
  analyticsProducts: true,
  experienceKeyMetrics: true,
  experienceCustomMetrics: true,
}).extend({
  id: z
    .uuid()
    .optional()
    .describe("UUID of the experience (auto-generated if not provided)"),
  productsLocation: S3FileLocationDtoSchema.nullish().describe(
    "S3 location for product data files"
  ),
  productType: z
    .enum(ProductVariantProductTypeList)
    .nullish()
    .describe("Type of products in the experience"),
  experienceIntegrations: CreateExperienceIntegrationDtoSchema.array()
    .default([])
    .describe("Third-party integrations connected to this experience"),
  experienceAnalysis: CreateExperienceAnalysisSchema.describe(
    "Analysis configuration for the experience"
  ),
});

export type CreateExperienceDto = z.infer<typeof CreateExperienceDtoSchema>;

export const PatchExperienceDtoSchema = z.object({
  name: z.string().optional(),
  isPreview: z.boolean().optional(),
  description: z.string().optional(),
  isDeleted: z.boolean().optional(),
  archivedAtTs: z.number().optional().nullish(),
  devicePreview: ExperienceDevicePreviewDtoSchema.optional(),
});

export type PatchExperienceDto = z.infer<typeof PatchExperienceDtoSchema>;

export const ExperienceVariationDtoScheme = z.object({
  experience: ExperienceDtoSchema,
  variation: VariationDtoSchema,
  offer: OfferDtoSchema,
});

export type ExperienceVariationDto = z.infer<
  typeof ExperienceVariationDtoScheme
>;
